import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import { calcUnsatisfiedQty } from '#/modules/momMgmt/workOrder/transfer/helper/transferShortage';

function displayQty(value) {
  if (value == null || value === '') {
    return '—';
  }
  return String(value);
}

/** 每行「庫存編號 {no} ．數量 {qty}」（全形．）；多行以 \\n 分隔。 */
function formatOperatingTip(lines = []) {
  return lines
    .map((line) => (
      `庫存編號 ${line.stockNo || '—'} ．數量 ${Number(line.qty) || 0}`
    ))
    .join('\n');
}

function OperatingQtyCell({ qty, tipLines = [] }) {
  const label = displayQty(qty);
  const tip = tipLines.length > 0 ? formatOperatingTip(tipLines) : null;
  const qtyClassName = 'w-fit font-medium text-primary';

  if (!tip) {
    return <p className={qtyClassName}>{label}</p>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <p className={cn(qtyClassName, 'cursor-help')}>{label}</p>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs whitespace-pre-line">
        {tip}
      </TooltipContent>
    </Tooltip>
  );
}

OperatingQtyCell.propTypes = {
  qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tipLines: PropTypes.arrayOf(PropTypes.shape({
    stockNo: PropTypes.string,
    qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })),
};

function materialRowClassName({ hasDraft, focused }) {
  if (focused) {
    return 'bg-primary-200/65 hover:bg-primary-200/65';
  }
  if (hasDraft) {
    return 'bg-primary-50/60 hover:bg-primary-50';
  }
  return 'hover:bg-slate-50';
}

const WorkOrderTransferMaterialList = ({
  materials = [],
  operatingByMtrId = {},
  draftLinesByMtrId = {},
  focusedMtrId = null,
  focusToken = 0,
  onOpenPicker,
  onFocusMaterial,
  emptyHint,
  keyword = '',
  onKeywordChange,
  keywordDisabled = false,
  showOnHandQty = true,
}) => {
  const rowRefs = useRef({});
  const showSearch = typeof onKeywordChange === 'function';
  const rowGridClassName = showOnHandQty
    ? 'grid-cols-[minmax(10rem,1.4fr)_repeat(4,minmax(4.5rem,1fr))_auto]'
    : 'grid-cols-[minmax(10rem,1.4fr)_repeat(3,minmax(4.5rem,1fr))_auto]';

  useEffect(() => {
    if (!focusedMtrId) {
      return;
    }
    const node = rowRefs.current[focusedMtrId];
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [focusedMtrId, focusToken, materials]);

  return (
    <>
      {showSearch ? (
        <div className="pb-3 flex min-w-0 items-center gap-1.5 sticky top-0 left-0 bg-panel z-10">
          <TableSearchInput
            useKeyword
            keyword={keyword}
            onKeywordChange={onKeywordChange}
            placeholder="搜尋料號、名稱"
            className="min-w-0 w-full max-w-md flex-1 text-foreground"
            disabled={keywordDisabled}
          />
        </div>
      ) : null}

      {materials.length === 0 ? (
        <p className="form-section__empty-state">
          {emptyHint || '請先選擇倉別以載入物料。'}
        </p>
      ) : (
        <ul className="m-0 min-w-[40rem] list-none divide-y divide-gray-200 border border-gray-200 rounded-md p-0">
          {materials.map((row, index) => {
            const operatingQty = Number(operatingByMtrId[row.mtrId]) || 0;
            const tipLines = draftLinesByMtrId[row.mtrId] ?? [];
            const hasDraft = tipLines.length > 0;
            const focused = focusedMtrId === row.mtrId;
            const unsatisfied = calcUnsatisfiedQty(
              row.requiredQty,
              row.onHandQty,
              operatingQty,
              { includeOnHand: showOnHandQty },
            );

            return (
              <li
                key={`${row.mtrId || 'mtr'}-${index}`}
                ref={(node) => {
                  if (node) {
                    rowRefs.current[row.mtrId] = node;
                  } else {
                    delete rowRefs.current[row.mtrId];
                  }
                }}
                className={cn(
                  'grid cursor-pointer items-center gap-2 px-3 py-3 transition-colors',
                  rowGridClassName,
                  materialRowClassName({ hasDraft, focused }),
                )}
                onClick={() => onFocusMaterial?.(row.mtrId)}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-800">
                    {row.mtrName || '（未命名）'}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {row.mtrNo || row.mtrId}
                  </p>
                </div>
                <div className="text-sm">
                  <p className="text-xs text-gray-500">當前需求</p>
                  <p className="font-medium">{displayQty(row.requiredQty)}</p>
                </div>
                {showOnHandQty ? (
                  <div className="text-sm">
                    <p className="text-xs text-gray-500">當前庫存</p>
                    <p className="font-medium">{displayQty(row.onHandQty)}</p>
                  </div>
                ) : null}
                <div className="text-sm">
                  <p className="text-xs text-gray-500">操作中</p>
                  <OperatingQtyCell qty={operatingQty} tipLines={tipLines} />
                </div>
                <div className="text-sm">
                  <p className="text-xs text-gray-500">尚未滿足</p>
                  <p className="font-medium text-amber-700">{displayQty(unsatisfied)}</p>
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenPicker(row);
                    }}
                  >
                    選庫
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
};

WorkOrderTransferMaterialList.propTypes = {
  materials: PropTypes.arrayOf(PropTypes.shape({
    mtrId: PropTypes.string,
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
    requiredQty: PropTypes.number,
    onHandQty: PropTypes.number,
    transferableInventoryStocks: PropTypes.array,
  })),
  operatingByMtrId: PropTypes.objectOf(PropTypes.number),
  draftLinesByMtrId: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({
    stockNo: PropTypes.string,
    qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }))),
  focusedMtrId: PropTypes.string,
  focusToken: PropTypes.number,
  onOpenPicker: PropTypes.func.isRequired,
  onFocusMaterial: PropTypes.func,
  emptyHint: PropTypes.string,
  keyword: PropTypes.string,
  onKeywordChange: PropTypes.func,
  keywordDisabled: PropTypes.bool,
  showOnHandQty: PropTypes.bool,
};

export default WorkOrderTransferMaterialList;
